import { useRef, useState } from 'react';

// material-ui
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import ProfileTab from './ProfileTab';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';

// assets
import avatar1 from 'assets/images/users/avatar-1.png';

import { useContext } from 'react';
import { UserContext } from '../../../../../contexts/UserContext';
import LogoutButton from '../../../../../pages/auth/Logout';
import { Button, Descriptions, Divider, Form, Input, Modal, Space } from 'antd';
import { sendPasswordEmail, updateUserProfile } from '../../../../../utils/api';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isViewProfileModalOpen, setIsViewProfileModalOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [profileForm] = Form.useForm();

  const { user: contextUser, loading, setUser, showToast } = useContext(UserContext);
  let user = contextUser;

  const SUPPORT_EMAIL = 'support@millwize.com';

  const isAdminUser = (record) =>
    record?.role === 'admin' ||
    record?.user_type === 'admin' ||
    record?.type === 'admin' ||
    record?.is_admin === true ||
    record?.is_admin === 1;

  const getCountryCode = (record) =>
    record?.country_code ?? record?.dial_code ?? record?.phone_country_code ?? record?.mobile_country_code;

  const getPhone = (record) => record?.phone ?? record?.mobile ?? record?.phone_number ?? record?.mobile_number;

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleProfileCancel = () => {
    setIsViewProfileModalOpen(false);
    setIsEditProfileModalOpen(false);
    profileForm.resetFields();
  };

  const openViewProfileModal = () => {
    profileForm.setFieldsValue({
      email: user?.email,
      country_code: getCountryCode(user),
      phone: getPhone(user)
    });
    setIsViewProfileModalOpen(true);
  };

  const openEditProfileModal = () => {
    profileForm.setFieldsValue({
      email: user?.email,
      country_code: getCountryCode(user),
      phone: getPhone(user)
    });
    setIsEditProfileModalOpen(true);
  };

  const handleResetPassword = async () => {
    if (!user?.email) {
      showToast?.('User email is missing', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await sendPasswordEmail({ email: user.email });
      showToast?.('Password reset email sent', 'success');
    } catch (error) {
      console.error('Failed to send reset password email:', error);
      showToast?.(error?.response?.data?.message || 'Failed to send reset email', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const updateProfile = async (values) => {
    const payload = {
      email: values?.email?.trim(),
      country_code: values?.country_code?.trim() || null,
      phone: values?.phone?.trim() || null
    };

    if (!payload.email) {
      showToast?.('Email is required', 'error');
      return;
    }

    setUpdatingProfile(true);
    try {
      const data = await updateUserProfile(payload);
      const updatedUser = data?.user ?? data?.data ?? data;

      if (updatedUser && typeof updatedUser === 'object') {
        setUser?.(updatedUser);
        localStorage.setItem('data', JSON.stringify(updatedUser));
        sessionStorage.setItem('data', JSON.stringify(updatedUser));
      }

      showToast?.(data?.message || 'Profile updated successfully', 'success');
      setIsEditProfileModalOpen(false);
      profileForm.resetFields();
    } catch (error) {
      console.error('Profile update failed:', error);

      const apiMessage = error?.response?.data?.message;
      const validationErrors = error?.response?.data?.errors;
      const firstValidationError =
        validationErrors && typeof validationErrors === 'object'
          ? Object.values(validationErrors)?.flat?.()?.[0]
          : null;

      showToast?.(firstValidationError || apiMessage || 'Failed to update profile', 'error');
    } finally {
      setUpdatingProfile(false);
    }
  };


  // Don't render anything if loading
  if (loading) return null;

  // Optional: fallback if user is missing
  if (!user) {
    const stored = localStorage.getItem('data') || sessionStorage.getItem('data');
    if (stored) {
      try {
        user = JSON.parse(stored);
      } catch {
        user = null;
      }
      if (user) setUser(user);
    }
  }
  if (!user) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2">User not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          bgcolor: open ? 'grey.100' : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter' },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2
          },
          ...theme.applyStyles('dark', {
            bgcolor: open ? 'background.default' : 'transparent',
            '&:hover': { bgcolor: 'secondary.light' }
          })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', p: 0.5 }}>
          <Avatar alt="profile user" src={avatar1} size="sm" />
          <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
            {user.name}
          </Typography>
        </Stack>
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [{ name: 'offset', options: { offset: [0, 9] } }]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({
              boxShadow: theme.customShadows.z1,
              width: 290,
              minWidth: 240,
              maxWidth: { xs: 250, md: 290 }
            })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <Avatar alt="profile user" src={avatar1} sx={{ width: 32, height: 32 }} />
                          <Stack>
                            <Typography variant="h6">{user.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.role}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid>
                        <Tooltip title="Logout">
                          <LogoutButton />
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <ProfileTab onEditProfile={openEditProfileModal} onViewProfile={openViewProfileModal} />
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      <Modal
        title={`View Profile - ${user?.name || user?.email || ''}`}
        open={isViewProfileModalOpen}
        onCancel={handleProfileCancel}
        footer={null}
        destroyOnHidden
      >
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Email">{user?.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="Phone">
            {getCountryCode(user) ? `${getCountryCode(user)} ` : ''}
            {getPhone(user) || '—'}
          </Descriptions.Item>
          {!isAdminUser(user) ? (
            <>
              <Descriptions.Item label="Support Email">{SUPPORT_EMAIL}</Descriptions.Item>
            </>
          ) : null}
        </Descriptions>

        <Divider />

        <Space>
          <Button onClick={handleResetPassword} loading={submitting}>
            Reset Password
          </Button>
          <Button onClick={handleProfileCancel}>Close</Button>
        </Space>
      </Modal>

      <Modal
        title={`Edit Profile - ${user?.name || user?.email || ''}`}
        open={isEditProfileModalOpen}
        onCancel={handleProfileCancel}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={updateProfile}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Invalid email' }
            ]}
          >
            <Input disabled={isAdminUser(user)} />
          </Form.Item>

          <Form.Item
            name="country_code"
            label="Country Code"
            rules={[
              {
                validator: (_, value) => {
                  const trimmed = value?.trim();
                  if (!trimmed) return Promise.resolve();
                  if (trimmed.length < 2) return Promise.reject(new Error('Country code must be at least 2 characters'));
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input disabled={isAdminUser(user)} placeholder="+91" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
            rules={[
              {
                validator: (_, value) => {
                  const trimmed = value?.trim();
                  if (!trimmed) return Promise.resolve();
                  if (trimmed.length < 10) return Promise.reject(new Error('Phone must be at least 10 characters'));
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input disabled={isAdminUser(user)} />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" loading={updatingProfile} disabled={updatingProfile}>
              Save
            </Button>
            <Button onClick={handleResetPassword} loading={submitting}>
              Reset Password
            </Button>
            <Button onClick={handleProfileCancel}>Close</Button>
          </Space>
        </Form>
      </Modal>
    </Box>
  );
}
